import { useState, useEffect, useCallback } from 'react';
import type { DelightPreferences } from '@/lib/delight/types';
import { DEFAULT_DELIGHT_PREFERENCES } from '@/lib/delight/types';

const STORAGE_KEY = 'delight-preferences';

/**
 * useDelightPreferences - Manage delight layer preferences
 *
 * Currently uses localStorage (like existing PreferencesSection pattern).
 * Future: Move to database for cross-device sync.
 */
export function useDelightPreferences() {
  const [preferences, setPreferences] = useState<DelightPreferences>(DEFAULT_DELIGHT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<DelightPreferences>;
        setPreferences({ ...DEFAULT_DELIGHT_PREFERENCES, ...parsed });
      }
    } catch (error) {
      console.error('Error loading delight preferences:', error);
    }
    setIsLoading(false);
  }, []);

  // Update a single preference
  const updatePreference = useCallback(
    <K extends keyof DelightPreferences>(key: K, value: DelightPreferences[K]) => {
      setPreferences((prev) => {
        const updated = { ...prev, [key]: value };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (error) {
          console.error('Error saving delight preferences:', error);
        }
        return updated;
      });
    },
    [],
  );

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setPreferences(DEFAULT_DELIGHT_PREFERENCES);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_DELIGHT_PREFERENCES));
    } catch (error) {
      console.error('Error resetting delight preferences:', error);
    }
  }, []);

  return {
    preferences,
    updatePreference,
    resetToDefaults,
    isLoading,
  };
}
